import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useServices } from "@/hooks/use-services";
import { ServiceCard } from "@/components/ServiceCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Car, Truck, Ship, Bike } from "lucide-react";
import { cn } from "@/lib/utils";

type VehicleFilter = 'all' | 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'caravan' | 'boat';

export default function Services() {
  const { data: services, isLoading } = useServices();
  const { t } = useTranslation();
  const [vehicleFilter, setVehicleFilter] = useState<VehicleFilter>('all');

  const filteredServices = useMemo(() => {
    if (!services) return [];
    const nonAddOns = services.filter(s => !s.isAddOn);

    if (vehicleFilter === 'all') return nonAddOns;
    // sedan/suv/cuv/motorcycle - show services with pricing > 0 for selected vehicle type
    return nonAddOns.filter(s =>
      s.vehiclePricing &&
      s.vehiclePricing[vehicleFilter] &&
      s.vehiclePricing[vehicleFilter] > 0
    );
  }, [services, vehicleFilter]);

  const filterOptions = useMemo(() => {
    const allOptions: { value: VehicleFilter; label: string; icon?: typeof Car }[] = [
      { value: 'all', label: t("booking.allVehicles") },
      { value: 'sedan', label: t("booking.sedan"), icon: Car },
      { value: 'cuv', label: t("booking.cuv"), icon: Car },
      { value: 'suv', label: t("booking.suv"), icon: Car },
      { value: 'van', label: t("booking.van"), icon: Truck },
      { value: 'motorcycle', label: t("booking.motorcycle"), icon: Bike },
      { value: 'caravan', label: t("booking.caravan"), icon: Truck },
      { value: 'boat', label: t("booking.boat"), icon: Ship },
    ];

    if (!services) return [{ value: 'all' as const, label: t("booking.allVehicles") }];

    const nonAddOns = services.filter(s => !s.isAddOn);

    // Check which vehicle types have services with pricing > 0
    const availableTypes = new Set<VehicleFilter>();

    (['sedan', 'suv', 'cuv', 'motorcycle', 'van', 'caravan', 'boat'] as const).forEach(type => {
      const hasServices = nonAddOns.some(s =>
        s.vehiclePricing &&
        s.vehiclePricing[type] &&
        s.vehiclePricing[type] > 0
      );
      if (hasServices) availableTypes.add(type);
    });

    // Return "All" + only available vehicle types
    return allOptions.filter(option =>
      option.value === 'all' || availableTypes.has(option.value)
    );
  }, [services, t]);

  return (
    <div className="min-h-screen pt-24 pb-24">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t("services.title")}</h1>
          <p className="text-lg text-muted-foreground">
            {t("services.subtitle")}
          </p>
        </div>

        {/* Vehicle Type Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {filterOptions.map((option) => (
            <Badge
              key={option.value}
              variant={vehicleFilter === option.value ? "default" : "outline"}
              className={cn(
                "cursor-pointer px-4 py-2 text-sm transition-all",
                vehicleFilter === option.value && "bg-primary text-primary-foreground"
              )}
              onClick={() => setVehicleFilter(option.value)}
              data-testid={`filter-${option.value}`}
            >
              {option.icon && <option.icon className="w-4 h-4 me-1.5" />}
              {option.label}
            </Badge>
          ))}
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-3xl border border-border bg-card p-6 h-[400px] flex flex-col">
                <Skeleton className="h-48 w-full rounded-2xl mb-6" />
                <Skeleton className="h-8 w-3/4 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6 mb-6" />
                <div className="mt-auto flex justify-between items-center">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-10 w-28 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} vehicleFilter={vehicleFilter} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
